import React from 'react';
import { ArrowLeft, Trash2, Search } from 'lucide-react';
import { applyShippingPromo } from './shipping';

// Header do cardápio público. Extraído VERBATIM de PublicMenu.js (Fase 2 refactor).
// Mantido como FUNÇÃO (chamado inline como {Header({...})}, não como componente JSX)
// para preservar exatamente o comportamento de render inline do componente pai.
const Header = ({ restaurant, currentScreen, navigateToScreen, getCartTotal, deliveryType, deliveryConfig, calculatedFee, selectedZone, cart, clearCart, openSearch, setShowStoreProfile, groupedProducts, handleCategoryClick, selectedCategoryFilter }) => (
    <div className="fixed top-0 left-0 right-0 z-50">
      {/* Banner de Loja Fechada Manualmente */}
      {restaurant?.manually_closed && (
        <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white px-4 py-3">
          <div className="flex items-center justify-center space-x-2">
            <div className="w-2 h-2 rounded-full bg-white animate-pulse"></div>
            <span className="font-semibold text-sm">
              🔒 LOJA FECHADA TEMPORARIAMENTE
            </span>
            <div className="w-2 h-2 rounded-full bg-white animate-pulse"></div>
          </div>
          <p className="text-center text-xs text-white/90 mt-1">
            Não estamos aceitando pedidos no momento
          </p>
        </div>
      )}
      {/* Blue section - restaurant name/logo and icons */}
      <div className="bg-primary text-white px-4 py-2">
        <div className="flex items-center justify-between">
          {/* Left side - Back button OR Logo + Name */}
          <div className="flex items-center space-x-2 flex-1">
            {currentScreen !== 'home' ? (
              <button 
                onClick={() => {
                  // Usa history.back() para respeitar o histórico de navegação
                  // Isso permite que o usuário volte para a tela correta
                  if (window.history.length > 1) {
                    window.history.back();
                  } else {
                    // Fallback se não há histórico
                    navigateToScreen('home');
                  }
                }}
                className="p-1"
              >
                <ArrowLeft size={20} />
              </button>
            ) : restaurant?.logo_url ? (
              // Logo circular no canto esquerdo
              <div className="w-10 h-10 bg-white rounded-full overflow-hidden flex-shrink-0 shadow-md">
                <img 
                  src={restaurant.logo_url} 
                  alt={restaurant.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // Se a logo falhar, esconde o container
                    e.target.parentElement.style.display = 'none';
                  }}
                />
              </div>
            ) : null}
            
            {/* Restaurant/Screen Name */}
            <h1 className={`text-lg font-medium ${currentScreen === 'home' && !restaurant?.logo_url ? 'flex-1' : ''}`}>
              {currentScreen === 'home' && (restaurant?.name || 'Carregando...')}
              {currentScreen === 'product' && 'Detalhes do Produto'}
              {currentScreen === 'cart' && 'Carrinho'}
              {currentScreen === 'promos' && 'Promoções'}
              {currentScreen === 'orders' && 'Meus Pedidos'}
              {currentScreen === 'checkout' && 'Finalizar Pedido'}
            </h1>
          </div>
          
          {/* Right side - Action buttons */}
          <div className="flex items-center space-x-3">
            {currentScreen === 'checkout' && (
              <span className="text-sm font-medium">Total: R$ {(() => {
                const _st = getCartTotal();
                const _base = deliveryType === 'delivery'
                  ? (deliveryConfig.mode === 'radius' ? (calculatedFee?.original_fee ?? calculatedFee?.fee ?? 0) : (selectedZone?.fee ?? 0))
                  : 0;
                const _pi = applyShippingPromo(_base, _st, deliveryConfig?.shipping_promo, deliveryType);
                return (_st + _pi.finalFee).toFixed(2);
              })()}</span>
            )}
            {currentScreen === 'cart' && cart.length > 0 && (
              <button 
                onClick={clearCart} 
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-500 hover:text-red-600 transition-all duration-200 text-sm font-medium border border-red-100 hover:border-red-200 active:scale-95"
              >
                <Trash2 size={14} />
                <span>Limpar</span>
              </button>
            )}
            {currentScreen === 'home' && (
              <button 
                onClick={openSearch}
                className="p-2 hover:bg-white/10 rounded-full transition-all active:scale-95"
                aria-label="Buscar produtos"
              >
                <Search size={20} />
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* White section - store information */}
      {currentScreen === 'home' && restaurant && (
        <div className={`text-gray-700 px-4 py-px border-b border-gray-200 ${
          restaurant.status?.is_open ? 'bg-white' : 'bg-red-50'
        }`}>
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center space-x-1.5">
              {restaurant.status?.is_expired || restaurant.status?.is_suspended ? (
                <>
                  <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span>
                  <span className="text-red-600 font-semibold">
                    {restaurant.status?.is_suspended ? '🔒 Loja Suspensa' : '⏰ Fechado - Renovação Pendente'}
                  </span>
                </>
              ) : restaurant.manually_closed ? (
                <>
                  <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse"></span>
                  <span className="text-orange-600 font-semibold">
                    🔒 Fechado Temporariamente
                  </span>
                </>
              ) : restaurant.status?.is_auto ? (
                <>
                  <span className={`w-2 h-2 rounded-full ${
                    restaurant.status.is_open ? 'bg-green-500' : 'bg-red-500'
                  }`}></span>
                  <span className={restaurant.status.is_open ? 'text-gray-600' : 'text-red-600 font-medium'}>
                    {restaurant.status.message}
                  </span>
                </>
              ) : (
                <span className="text-gray-600">Aberto</span>
              )}
              {!restaurant.status?.is_expired && !restaurant.status?.is_suspended && (
                <>
                  <span className="text-gray-400">•</span>
                  <span className="text-gray-600">Pedido mín. R$ {restaurant.minimum_order?.toFixed(2) || '15,00'}</span>
                </>
              )}
            </div>
            <button 
              className="text-primary font-medium"
              onClick={() => setShowStoreProfile(true)}
            >
              Perfil da loja
            </button>
          </div>
        </div>
      )}

      {/* Categories horizontal scroll */}
      {currentScreen === 'home' && groupedProducts.length > 0 && (
        <div className="bg-white border-b border-gray-200 overflow-x-auto scrollbar-hide">
          <div className="flex px-4 space-x-4 min-w-max">
            <button
              id="cat-btn-all"
              onClick={() => handleCategoryClick('all')}
              className={`py-2 text-sm font-medium whitespace-nowrap relative transition-colors ${
                selectedCategoryFilter === 'all'
                  ? 'text-primary'
                  : 'text-gray-700'
              }`}
            >
              Todos
              {selectedCategoryFilter === 'all' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
              )}
            </button>
            {groupedProducts.map((category) => (
              <button
                key={category.id}
                id={`cat-btn-${category.id}`}
                onClick={() => handleCategoryClick(category.id)}
                className={`py-2 text-sm font-medium whitespace-nowrap relative transition-colors ${
                  selectedCategoryFilter === category.id
                    ? 'text-primary'
                    : 'text-gray-700'
                }`}
              >
                {category.name}
                {selectedCategoryFilter === category.id && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
);

export default Header;
